import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { 
  Grid, 
  GridColumn, 
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridCellProps,
  GridHandle,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { Groups, Endpoints, GroupStatus } from '../../../../Models/Assets/AssetTemplateReadDto';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

interface Props {
  groups: Groups[];
  onGroupSelect?: (groupId: string | null) => void;
  selectedGroupId?: string | null;
  onGroupsChange: (groups: Groups[]) => void;
  endpoints?: Endpoints[];
  onEndpointsChange?: (endpoints: Endpoints[]) => void;
  onAddGroup?: () => void;
  instructionText?: string;
  /** Edit template: delete sets status=Deleted (soft). Create template: delete removes from list (hard). */
  isEditTemplate?: boolean;
}

const DATA_ITEM_KEY = 'id';
const INITIAL_SORT: SortDescriptor[] = [{ field: 'name', dir: 'asc' }];

function isGroupActive(group: Groups): boolean {
  if (group.status !== undefined) return group.status === 'Active';
  return group.isEnabled !== false;
}

function isGroupNameDuplicate(newName: string, groups: Groups[], excludeGroupId: string): boolean {
  const normalized = (newName || '').trim().toLowerCase();
  if (!normalized) return false;
  return groups.some(
    (g) => isGroupActive(g) && g.id !== excludeGroupId && (g.name || '').trim().toLowerCase() === normalized
  );
}

const GroupsTableTemplateComponent: React.FC<Props> = ({
  groups,
  onGroupSelect,
  selectedGroupId,
  onGroupsChange,
  endpoints = [],
  onEndpointsChange,
  onAddGroup,
  instructionText,
  isEditTemplate = false
}) => {
  const gridRef = useRef<GridHandle>(null);
  const [select, setSelect] = React.useState<SelectDescriptor>(() => {
    return selectedGroupId ? { [selectedGroupId]: true } : {};
  });
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortDescriptor[]>(INITIAL_SORT);
  const [orderedRowKeys, setOrderedRowKeys] = React.useState<string[]>([]);
  const localizationService = useLocalization();
  const previousVisibleKeysSignatureRef = useRef<string>('');
  const previousSortSignatureRef = useRef<string>('');

  const activeGroups = useMemo(() => groups.filter(isGroupActive), [groups]);
  const activeGroupKeysSignature = useMemo(
    () => activeGroups.map((group) => group.id).join('|'),
    [activeGroups]
  );

  useEffect(() => {
    const sortSignature = JSON.stringify(sort);
    const didSortChange = previousSortSignatureRef.current !== sortSignature;
    const didVisibleRowsChange = previousVisibleKeysSignatureRef.current !== activeGroupKeysSignature;

    if (!didSortChange && !didVisibleRowsChange) {
      return;
    }

    const sortedGroups = sort.length > 0 ? orderBy(activeGroups, sort) : activeGroups;
    setOrderedRowKeys(sortedGroups.map((group) => group.id));
    previousSortSignatureRef.current = sortSignature;
    previousVisibleKeysSignatureRef.current = activeGroupKeysSignature;
  }, [activeGroupKeysSignature, activeGroups, sort]);

  const editableGroups = useMemo(() => {
    const groupsById = new Map(activeGroups.map((group) => [group.id, group]));
    const orderedGroups = orderedRowKeys.length > 0
      ? orderedRowKeys
        .map((groupId) => groupsById.get(groupId))
        .filter((group): group is Groups => group !== undefined)
      : (sort.length > 0 ? orderBy(activeGroups, sort) : activeGroups);

    return orderedGroups.map(group => ({ ...group, inEdit: true }));
  }, [activeGroups, orderedRowKeys, sort]);

  useEffect(() => {
    if (selectedGroupId === '0') {
      const newSelect: SelectDescriptor = {};
      activeGroups.forEach((group: Groups) => {
        newSelect[group.id] = true;
      });
      setSelect(newSelect);
    } else if (selectedGroupId) {
      setSelect({ [selectedGroupId]: true });
      const selectedIndex = editableGroups.findIndex(group => group.id === selectedGroupId);
      if (selectedIndex >= 0 && gridRef.current) {
        gridRef.current.scrollIntoView({ rowIndex: selectedIndex });
      }
    } else {
      setSelect({});
    }
  }, [selectedGroupId, activeGroups, editableGroups]);

  const handleSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const selectedGroup = event.dataItem || event.startDataItem;
    event.nativeEvent?.stopPropagation();
    if (selectedGroup) {
      const newGroupId = selectedGroup.id;
      if (newGroupId !== selectedGroupId) {
        setSelect({ [newGroupId]: true });
        onGroupSelect?.(newGroupId);
      }
    }
  }, [selectedGroupId, onGroupSelect]);

  const handleHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    event.nativeEvent?.stopPropagation();
    const isSelected = event.nativeEvent.target.checked;
    const newSelect: SelectDescriptor = {};
    if (isSelected) {
      activeGroups.forEach((group: Groups) => {
        newSelect[group.id] = true;
      });
      onGroupSelect?.('0');
    } else {
      onGroupSelect?.(null);
    }
    setSelect(newSelect);
  }, [activeGroups, onGroupSelect]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    const dataItem = event.dataItem as Groups;
    const newGroups = groups.map((group: Groups) =>
      group.id === dataItem.id ? { ...group, [event.field!]: event.value } : group
    );
    onGroupsChange(newGroups);

    if (event.field === 'name') {
      const newName = (event.value as string) ?? '';
      if (!newName.trim()) {
        setNameError(localizationService.toLanguageString('custom.uniqueName', enMessages.custom.uniqueName));
      } else if (isGroupNameDuplicate(newName, newGroups, dataItem.id)) {
        setNameError(localizationService.toLanguageString('custom.nameExistsInTemplate', 'This name already exists in this template. Names must be unique.'));
      } else {
        setNameError(null);
      }
    }
  };


  const pointsCell = (props: GridCellProps) => {
    // Count only non-deleted endpoints associated with this group
    const endpointCount = endpoints.filter(
      (endpoint: Endpoints) =>
        endpoint.groupId === props.dataItem.id && endpoint.status !== 'Deleted'
    ).length;
    return <td><div>{endpointCount}</div></td>;
  };

  const EmptyHeaderCell = () => <th></th>;

  const handleDeleteGroup = (groupId: string) => {
    if (isEditTemplate) {
      const updatedGroups = groups.map(g =>
        g.id === groupId ? { ...g, status: 'Deleted' as GroupStatus } : g
      );
      onGroupsChange(updatedGroups);
    } else {
      const updatedGroups = groups.filter(group => group.id !== groupId);
      onGroupsChange(updatedGroups);
      if (onEndpointsChange) {
        const updatedEndpoints = endpoints.filter((endpoint: Endpoints) => endpoint.groupId !== groupId);
        onEndpointsChange(updatedEndpoints);
      }
    }
    if (selectedGroupId === groupId) {
      onGroupSelect?.(null);
    }
  };

  const deleteCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          type="button"
          themeColor="primary"
          svgIcon={trashIcon}
          title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteGroup(dataItem.id);
          }}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">{localizationService.toLanguageString('custom.groups', enMessages.custom.groups)}</div>
        <div className="flex items-center gap-2">
          {onAddGroup && (
            <Button 
              type="button"
              themeColor="primary"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              svgIcon={plusIcon}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddGroup();
              }}
            />
          )}
        </div>
      </div>
      {instructionText && (
        <p className="text-xs text-gray-500 mb-2 px-1">{instructionText}</p>
      )}
      {nameError && (
        <p className="text-xs text-red-600 mb-2 px-1" style={{ marginBottom: 0 }}>* {nameError}</p>
      )}
      <div className="flex-1 min-h-0 overscroll-contain checkbox-outline-soft-override">
        <Grid
          ref={gridRef}
          data={editableGroups}
          style={{ height: '100%' }}
          dataItemKey={DATA_ITEM_KEY}
          selectable={{
            enabled: true,
            mode: 'single'
          }}
          select={select}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onItemChange={handleItemChange}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
          editable={true}
          editField="inEdit"
        >
          <GridColumn 
            columnType="checkbox" 
            width="50px"
            cells={{
              headerCell: EmptyHeaderCell
            }}
            editable={false}
          />
          <GridColumn 
            field="name" 
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
          />
          <GridColumn 
            field="points" 
            title={localizationService.toLanguageString('custom.points', enMessages.custom.points)}
            cells={{
              data: pointsCell
            }}
            editable={false}
            width={"100px"}
          />
          {onAddGroup && (
            <GridColumn
              title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
              width="100px"
              sortable={false}
              filterable={false}
              editable={false}
              cells={{
                data: deleteCell
              }}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default GroupsTableTemplateComponent;
